import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Package, Fish, Users, Building2, ArrowRight, Clock } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const statusLabels = {
  documentacion_inicial: "Documentación inicial",
  en_planta: "En la planta",
  en_reconocimiento: "En reconocimiento",
  listo_para_cargar: "Listo para cargar",
  en_transito: "En tránsito",
  en_destino: "En destino",
  entregado_cliente: "Entregado a cliente",
  completado: "Completado",
  anulado: "Anulado",
  rechazado: "Rechazado",
  otros: "Otros",
  pendiente: "Documentación inicial",
  recibido: "En destino",
};

const statusColors = {
  documentacion_inicial: "bg-amber-100 text-amber-800",
  en_planta: "bg-orange-100 text-orange-800",
  en_reconocimiento: "bg-yellow-100 text-yellow-800",
  listo_para_cargar: "bg-lime-100 text-lime-800",
  en_transito: "bg-sky-100 text-sky-800",
  en_destino: "bg-cyan-100 text-cyan-800",
  entregado_cliente: "bg-teal-100 text-teal-800",
  completado: "bg-emerald-100 text-emerald-800",
  anulado: "bg-gray-100 text-gray-800",
  rechazado: "bg-red-100 text-red-800",
  otros: "bg-purple-100 text-purple-800",
  pendiente: "bg-amber-100 text-amber-800",
  recibido: "bg-cyan-100 text-cyan-800",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Verificar si el usuario es cliente
  const isClient = user?.role === "cliente";

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get("/stats/dashboard");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1">Resumen de actividad y estadísticas</p>
      </div>

      {/* KPI Cards - Grid dinámico según rol */}
      {/* Admin/Admin_consultor: 4 KPIs, Proveedor: 3 KPIs, Cliente: 2 KPIs */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${
        user?.role === "cliente" ? 'lg:grid-cols-2' : 
        user?.role === "proveedor" ? 'lg:grid-cols-3' : 
        'lg:grid-cols-4'
      } gap-4`}>
        <Card 
          className="kpi-card card-hover cursor-pointer transition-transform hover:scale-[1.02]" 
          data-testid="kpi-batches"
          onClick={() => navigate("/batches")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-value">{stats?.total_batches || 0}</p>
                <p className="kpi-label">Lotes Totales</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-sky-100 flex items-center justify-center">
                <Package className="w-6 h-6 text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="kpi-card card-hover cursor-pointer transition-transform hover:scale-[1.02]" 
          data-testid="kpi-products"
          onClick={() => navigate("/products")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="kpi-value">{stats?.total_products || 0}</p>
                <p className="kpi-label">Productos</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-emerald-100 flex items-center justify-center">
                <Fish className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Clientes - Solo admin y admin_consultor */}
        {(user?.role === "admin" || user?.role === "admin_consultor") && (
          <Card 
            className="kpi-card card-hover cursor-pointer transition-transform hover:scale-[1.02]" 
            data-testid="kpi-clients"
            onClick={() => navigate("/clients")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="kpi-value">{stats?.total_clients || 0}</p>
                  <p className="kpi-label">Clientes</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-violet-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-violet-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Proveedores - Solo admin, admin_consultor y proveedor */}
        {(user?.role === "admin" || user?.role === "admin_consultor" || user?.role === "proveedor") && (
          <Card 
            className="kpi-card card-hover cursor-pointer transition-transform hover:scale-[1.02]" 
            data-testid="kpi-suppliers"
            onClick={() => navigate("/suppliers")}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="kpi-value">{stats?.total_suppliers || 0}</p>
                  <p className="kpi-label">Proveedores</p>
                </div>
                <div className="w-12 h-12 rounded-sm bg-amber-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Status breakdown and Recent batches */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status breakdown */}
        <Card className="border-slate-200" data-testid="status-breakdown">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">Estado de Lotes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(statusLabels)
              .filter(([key]) => !['pendiente', 'recibido'].includes(key)) // Excluir estados legacy
              .map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${statusColors[key].replace('text-', 'bg-').split(' ')[0]}`} />
                  <span className="text-sm text-slate-600">{label}</span>
                </div>
                <span className="font-mono font-medium text-slate-900">
                  {stats?.batches_by_status?.[key] || 0}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent batches */}
        <Card className="border-slate-200 lg:col-span-2" data-testid="recent-batches">
          <CardHeader className="pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Lotes Recientes</CardTitle>
            <Link
              to="/batches"
              className="text-sm text-sky-600 hover:text-sky-700 flex items-center gap-1"
            >
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent_batches?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_batches.map((batch) => (
                  <Link
                    key={batch.id}
                    to={`/batches/${batch.id}`}
                    className="flex items-center justify-between p-3 rounded-sm border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-sm bg-slate-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-mono font-medium text-slate-900">
                          {batch.numero_lote}
                        </p>
                        <p className="text-sm text-slate-500">
                          {batch.producto_nombre || "Sin producto"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={statusColors[batch.estado]}>
                        {statusLabels[batch.estado]}
                      </Badge>
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1 justify-end">
                        <Clock className="w-3 h-3" />
                        {batch.created_at
                          ? format(new Date(batch.created_at), "dd MMM yyyy", { locale: es })
                          : "-"}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay lotes registrados</p>
                <Link
                  to="/batches/new"
                  className="text-sky-600 hover:underline text-sm mt-2 inline-block"
                >
                  Crear primer lote
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
